import { create } from "zustand";
import { persist } from "zustand/middleware";
import api from "@/api/client";
import { sentryIdentifyUser, sentryClearUser } from "@/sentry";

/* ── SSO bridge: share token with other StudoX apps ────────────────────── */
function writeSSOToken(token) {
  try { localStorage.setItem("studox-core-sso-v1", JSON.stringify({ token, ts: Date.now() })); } catch {}
}
function clearSSOToken() {
  try { localStorage.removeItem("studox-core-sso-v1"); } catch {}
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      isFirstLogin: false,

      login: async ({ name, plz, code, institutionId }) => {
        const { data } = await api.post("/auth/login", { name, plz, code, institutionId });
        set({ token: data.token, user: data.user, isAuthenticated: true, isFirstLogin: data.isNew === true });
        sentryIdentifyUser(data.user);
        writeSSOToken(data.token);
        return data;
      },

      clearFirstLogin: () => set({ isFirstLogin: false }),

      logout: () => {
        sentryClearUser();
        clearSSOToken();
        set({ token: null, user: null, isAuthenticated: false });
      },

      // Atomically install a session (used by the SSO handoff). Writes the shared
      // SSO token so every other StudoX app recognises the session too.
      setSession: ({ token, user }) => {
        set({ token, user, isAuthenticated: true });
        sentryIdentifyUser(user);
        writeSSOToken(token);
      },

      // SOFT refresh — used by sockets / post-login. It NEVER logs out on failure.
      // (A transient network blip must not destroy a valid session — that was the
      // root of the "Flow shows briefly, then bounces back to Core" crash loop.)
      // Returns true on success, false otherwise.
      refreshMe: async () => {
        try {
          const { data } = await api.get("/auth/me");
          set({ user: data.user });
          sentryIdentifyUser(data.user);
          return true;
        } catch {
          return false;
        }
      },

      // HARD session verification for the boot gate. Distinguishes an INVALID token
      // (401/403 → clear the session) from a BACKEND problem (network/5xx/timeout →
      // keep the session, return "error" so the gate can route to status.studox.eu).
      // Children's data only renders after this returns "ok".
      verifySession: async () => {
        const token = get().token;
        if (!token) return "invalid";
        try {
          const { data } = await api.get("/auth/me");
          set({ user: data.user, isAuthenticated: true });
          sentryIdentifyUser(data.user);
          writeSSOToken(token);
          return "ok";
        } catch (e) {
          const status = e?.response?.status;
          if (status === 401 || status === 403) { get().logout(); return "invalid"; }
          return "error"; // backend down / network / 5xx → do NOT destroy the session
        }
      },

      updateAvatar: (avatar) => set((s) => ({ user: { ...s.user, avatar } })),

      // Instantly update coins/XP in the store (called from socket events)
      updateStudentStats: ({ xp, coins }) => set((s) => {
        if (!s.user?.studentProfile) return {};
        return {
          user: {
            ...s.user,
            studentProfile: {
              ...s.user.studentProfile,
              ...(xp    !== undefined && { xp }),
              ...(coins !== undefined && { coins }),
            },
          },
        };
      }),

      updateSettings: async (settings) => {
        const { data } = await api.patch("/users/me/settings", settings);
        set((s) => ({ user: { ...s.user, settings: data.settings } }));
        return data;
      },
    }),
    {
      name: "studox-flow-auth",
      partialize: (s) => ({ token: s.token, user: s.user, isAuthenticated: s.isAuthenticated, isFirstLogin: s.isFirstLogin }),
    }
  )
);
